import { themesList } from 'constants/themesList';
import { Popover } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { RootState } from 'store/configureStore';
import { useSelector } from 'react-redux';
import { InfoType } from 'store/ducks/Global/types';
import * as Styled from '../Tables/Reporting/RiskDashboard/Tree.styled';
import { useTranslation } from 'react-i18next';

export const getGlassColor = (color: string, alpha = 0.08) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const NodeCard = ({
  label,
  value,
  info,
}: {
  label: string;
  value: string;
  info?: InfoType;
}) => {
  const { scheme } = useSelector((state: RootState) => state.global);
  const currentTheme = themesList[scheme as keyof typeof themesList];
  const { t } = useTranslation();
  const baseColor = currentTheme.colorPrimary;
  const treeTextColor =
    (currentTheme as Record<string, string>).treeTextColor || baseColor;
  const treeBorderColor =
    (currentTheme as Record<string, string>).treeBorderColor || baseColor;
  const treeTitleBg =
    (currentTheme as Record<string, string>).treeTitleBg || baseColor;

  const style = {
    bg: getGlassColor(baseColor, 0.08),
    border: getGlassColor(treeBorderColor, 0.22),
    text: treeTextColor,
    badge: treeTitleBg,
  };

  const isFunctionContext =
    label === 'Function' ||
    (label === 'Data' && !!info?.function_location_of_data_at_rest_name);

  const isServiceContext =
    label === 'ICT Service' ||
    (label === 'Data' && !!info?.ict_service_location_of_data_at_rest_name);

  const popoverContent = info ? (
    <Styled.PopoverContentDiv>
      <div>
        <strong>{t('TREE_NODE_CARD.LOCATION_OF_DATA_AT_REST')}</strong>
        <span>
          {isFunctionContext
            ? info.function_location_of_data_at_rest_name
            : info.ict_service_location_of_data_at_rest_name || '—'}
        </span>
      </div>

      <div>
        <strong>{t('TREE_NODE_CARD.LOCATION_OF_DATA_MANAGEMENT')}</strong>{' '}
        <span>
          {isFunctionContext
            ? info.function_location_of_data_management_name
            : info.ict_service_location_of_data_management_name || '—'}
        </span>
      </div>

      <div>
        <strong>{t('TREE_NODE_CARD.SITE')}</strong>{' '}
        <span>
          {isFunctionContext
            ? info.function_data_site
            : info.ict_service_data_site || '—'}
        </span>
      </div>

      <div>
        <strong>{t('TREE_NODE_CARD.SENSITIVENESS')}</strong>{' '}
        <span>
          {isFunctionContext
            ? info.function_sensitiveness_of_data_stored_by_ict_tpsp_name
            : info.ict_service_sensitiveness_of_data_stored_by_ict_tpsp_name ||
              '—'}
        </span>
      </div>
    </Styled.PopoverContentDiv>
  ) : null;

  const hasInfo = !!(
    info?.function_location_of_data_at_rest_name ||
    info?.ict_service_location_of_data_at_rest_name ||
    info?.function_location_of_data_management_name ||
    info?.ict_service_location_of_data_management_name ||
    info?.function_data_site ||
    info?.ict_service_data_site ||
    info?.function_sensitiveness_of_data_stored_by_ict_tpsp_name ||
    info?.ict_service_sensitiveness_of_data_stored_by_ict_tpsp_name
  );

  const isInteractive = !!info?.interactive;

  return (
    <Styled.NodeContainer>
      <Styled.LabelBadge color={style.badge}>{label}</Styled.LabelBadge>

      <Styled.ValueCard
        $bg={style.bg}
        $border={style.border}
        $text={style.text}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>{value}</span>

          {label === 'Data' && isInteractive && (
            <PlusOutlined style={{ color: style.text }} />
          )}
        </div>

        {hasInfo && (
          <Popover content={popoverContent} trigger="click">
            <Styled.InfoIcon color={style.text} />
          </Popover>
        )}
      </Styled.ValueCard>
    </Styled.NodeContainer>
  );
};
